import type { EntryKey } from "@pinia/colada";
import type { User, UserRole, UserStatus, UserWithRole } from "@/schemas/user.schema";
import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import { membersApi } from "@/api/members.api";
import { PROJECTS_QUERY_KEYS } from "@/queries/projects.query";
import { SUBSCRIPTION_LIMITS_QUERY_KEYS } from "@/queries/subscription-limits.query";
import { api } from "@/services/api";
import { hasPermission } from "@/stores/user-profile.store";

/**
 * Query keys factory for members following Pinia Colada best practices
 *
 * @see https://pinia-colada.esm.dev/guide/query-keys.html
 */
export const MEMBERS_QUERY_KEYS = {
  root: ["members"] as const,
  list: () => [...MEMBERS_QUERY_KEYS.root, "list"] as const,
};

/**
 * Query hook for fetching organization members
 *
 * Features:
 * - Smart caching with 5min stale time
 * - Automatic deduplication of concurrent requests
 * - Enabled only for users with members:read permission
 *
 * @example
 * ```ts
 * const membersQuery = useMembersQuery();
 *
 * // In template
 * if (membersQuery.isLoading.value) return <Spinner />;
 * const members = membersQuery.data.value;
 * ```
 */
export function useMembersQuery() {
  return useQuery({
    key: MEMBERS_QUERY_KEYS.list() as unknown as EntryKey,
    query: async () => {
      const { data } = await api.get<UserWithRole[]>("/user-organizations");
      return data;
    },
    // Only fetch for users with members:read permission (developer+)
    enabled: () => hasPermission("members:read"),
    staleTime: 5 * 60_000, // 5 minutes - data is considered fresh
    gcTime: 10 * 60_000, // 10 minutes - garbage collection time
  });
}

/**
 * Helper hook to invalidate members queries
 *
 * Use this after mutations that affect members (add/remove/update)
 *
 * @example
 * ```ts
 * const invalidate = useInvalidateMembers();
 *
 * // After adding/removing member
 * const handleMemberUpdate = async () => {
 *   await updateMember(...);
 *   invalidate.all();
 * };
 * ```
 */
export function useInvalidateMembers() {
  const queryCache = useQueryCache();

  return {
    /**
     * Invalidate all members queries
     */
    all: () => queryCache.invalidateQueries({ key: MEMBERS_QUERY_KEYS.root as any }),

    /**
     * Invalidate only members list query
     */
    list: () => queryCache.invalidateQueries({ key: MEMBERS_QUERY_KEYS.list() as any }),
  };
}

/**
 * Mutation to create a new organization member
 *
 * AI-196 Phase 2: For project-scope roles (developer, member, viewer, guest),
 * project_ids is required to specify which projects the user can access.
 * All project IDs are now real UUIDs (including Default Workspace).
 *
 * @example
 * ```ts
 * const createMember = useCreateMember();
 * await createMember.mutateAsync({
 *   role: "developer",
 *   user: { full_name: "John Doe", email: "john@example.com", ... },
 *   notify_by_email: true,
 *   project_ids: ["proj-123", "proj-456"] // Required for project-scope roles
 * });
 * // Cache automatically invalidated
 * ```
 */
export function useCreateMember() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: {
      role: UserRole;
      user: Omit<User, "id" | "created_at" | "updated_at" | "deleted_at">;
      notify_by_email?: boolean;
      project_ids?: string[];
    }) => {
      return await membersApi.create(
        payload.role,
        payload.user,
        {
          notify_by_email: payload.notify_by_email ?? true,
        },
        payload.project_ids
      );
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: MEMBERS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Mutation to invite a member to the organization (simplified flow).
 * Only requires email + role. Backend handles existing vs new user logic.
 */
export function useInviteMember() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: {
      email: string;
      role: UserRole;
      project_ids?: string[];
    }) => {
      return await membersApi.invite(
        payload.email,
        payload.role,
        payload.project_ids
      );
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: MEMBERS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Mutation to restore a previously removed member by email
 *
 * @example
 * ```ts
 * const restoreMember = useRestoreMemberByEmail();
 * await restoreMember.mutateAsync("user@example.com");
 * ```
 */
export function useRestoreMemberByEmail() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (email: string) => {
      return await membersApi.restoreByEmail(email);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: MEMBERS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Mutation to update a member's role
 *
 * @example
 * ```ts
 * const updateRole = useUpdateMemberRole();
 * await updateRole.mutateAsync({ id: "user-123", role: "admin" });
 * ```
 */
export function useUpdateMemberRole() {
  return useMutation({
    mutation: async (payload: { id: string; role: UserRole }) => {
      return await membersApi.updateRole(payload.id, payload.role);
    },
    // Note: no onSuccess — callers handle query invalidation (EditTeamMember, etc.)
  });
}

/**
 * Mutation to update a member's status (active/suspended)
 *
 * @example
 * ```ts
 * const updateStatus = useUpdateMemberStatus();
 * await updateStatus.mutateAsync({ id: "user-123", status: "suspended" });
 * ```
 */
export function useUpdateMemberStatus() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: { id: string; status: UserStatus }) => {
      return await membersApi.updateStatus(payload.id, payload.status);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: MEMBERS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Mutation to delete a member from the organization
 *
 * @example
 * ```ts
 * const deleteMember = useDeleteMember();
 * await deleteMember.mutateAsync("user-123");
 * ```
 */
export function useDeleteMember() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (id: string) => {
      return await membersApi.delete(id);
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: MEMBERS_QUERY_KEYS.root as any });
      // Also invalidate project members cache since deleted user may appear in project member lists
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Mutation to change a member's password (admin action)
 */
export function useChangeMemberPassword() {
  return useMutation({
    mutation: async (payload: { id: string; password: string }) => {
      return await membersApi.changePassword(payload.id, payload.password);
    },
  });
}
